import styles from "./DrawDetailCard.module.scss";
import { FC } from "react";
import { DrawMethod, DrawType, IDraw } from "../../pages/DrawPage/DrawItem";
import backgroundImage from './icons/background.png';
import { unixToDrawString, unixToShortString } from "../../utils/time";
import { trueCandidateForm_2, trueWinnerForm_2 } from "../../utils/true.word.form";
import { ChannelLink } from "../Links/ChannelLink/ChannelLink";
import { useDrawNavigate } from "../../hooks/navigate";
import { DrawContext } from "../../pages/DrawPage/DrawRoutes";
import { getPlatformText } from "../../utils/platform-text";
import { getPolicyLink } from "../../utils/links";
import { Link } from "../Links/ChannelLink/Link";

interface IDrawDetailCardProps {
    draw: IDraw;
}

export const DrawDetailCard: FC<IDrawDetailCardProps> = ({ draw }) => {    
    const navigate = useDrawNavigate();

    const isInteractive = () => draw.method === DrawMethod.Interactive;

    const channel = (caption: string) => <ChannelLink caption={ caption } />;

    const getTitle = (name: string) => `Разыгрывается ${name.endsWith('.') ? name.slice(0, -1) : name}!`;

    const getAction = () => {
        const drawStartTime = unixToDrawString(draw.operDate).split(' ').join(' в ');
        
        const winnerForm = isInteractive() 
            ? trueCandidateForm_2(draw.prizeCount)
            : trueWinnerForm_2(draw.prizeCount);
        
        const pronoun = draw.prizeCount === 1 ? (isInteractive() ? 'его' : 'ему') 
                                    : (isInteractive() ? 'их' : 'им');

        const channelElement = channel(getPlatformText("channelIn"));

        return (
            <>
                {`${drawStartTime} мы случайным образом выберем ${draw.prizeCount} `}
                {winnerForm}
                {isInteractive()
                    ? ` и объявим ${pronoun} на нашем `
                    : ` и отправим ${pronoun} подарок в личном сообщении.`
                }
                {isInteractive() && channelElement}
                {isInteractive() && '.'}
            </>
        );
    };

    const getInteractiveAttention = () => (
        <ul>
            <li>{`После объявления у победителя будет ${draw.confirmMinutesPeriod} минут, чтобы зайти в игру и забрать подарок.`}</li>
            <li>Если победитель не успеет, подарок разыграют снова среди остальных участников.</li>
        </ul>
    );

    const getAutoAttention = (drawType: DrawType) => {
        let typeStr: string = '';
        switch (drawType) {
            case DrawType.Rare: typeStr = 'редкой'; break;
            case DrawType.Epic: typeStr = 'эпической'; break;
            case DrawType.Legendary: typeStr = 'легендарной'; break;
        }

        return (
            <ul>
                <li>{`Шанс на победу зависит от количества билетов, потраченных на розыгрыш, и рейтинга ${typeStr} удачи.`}</li>
                <li>Рейтинг удачи увеличивается при каждом проигрыше, пропорционально кол-ву потраченных билетов.</li>
                <li>Чем больше билетов тратит игрок на розыгрыш — тем быстрее растет его рейтинг и шансы на победу.</li>
                <li>
                    Результаты розыгрыша будут опубликованы на нашем{' '}
                    {channel(getPlatformText("channelIn"))}{' '}
                    {unixToShortString(draw.operDate)} в 12:00.
                </li>
            </ul>
        );
    }

    const getAttention = (drawType: DrawType) => (
        <>
            {'❗️ Важно:'}
            {isInteractive() ? getInteractiveAttention() : getAutoAttention(drawType)}
        </>
    );

    const getFooter = () => {
        return (
            <>
                <p>
                    {`Подпишитесь на наш `}{channel(getPlatformText("channelName"))}{` и включите уведомления, чтобы не пропустить `}{isInteractive()
                    ? `розыгрыш!`
                    : `публикацию результатов розыгрыша!`
                    }
                </p>
                <p>
                    <Link path={getPolicyLink()} text="Положение о проведении стимулирующего мероприятия" newTab />.
                </p>
            </>
        );
    }

    const getDrawDescirption = () => {
        const result = [];
        result.push(getTitle(draw.prizeName));
        result.push(getAction());
        result.push(getAttention(draw.type));
        result.push(getFooter());

        return result;
    }

    return (
        <>        
            <div className={styles.card}>                            
                <img src={ backgroundImage } alt={ draw?.prizeName } />
                <p className={styles.text}>{ draw?.prizeName }</p>
            </div>

            <div className={`${styles.card}`} >

                <div className={styles.description}>
                    { getDrawDescirption().map((text, index) => <p key={`draw-desc-p${index}`}>{text}</p>) }
                </div>

                <button
                    className={ `${styles.btn}` }
                    onClick={ () => navigate(isInteractive() ? DrawContext.Interactive : DrawContext.Automatic, draw.drawId) }
                >
                    { 'Закрыть' }
                </button>
            </div>
        </>
    );
};